"use client";
import React, { useState, useEffect, Fragment } from "react";
import Select from "react-select";
import { useSelector } from "react-redux";
import { ChevronDown, ChevronRight } from "lucide-react";
import CollapsibleSections from "../../SupplierProfile/DiversityCollapsibleSections";
import { Dialog, Transition } from "@headlessui/react";

const PersonalInfoPreview = ({ personalInfo }) => {
  const {
    profilePicture = "/default-profile.png",
    firstName = "John",
    lastName = "Doe",
    country = "United States",
    stateProvince = "California",
    city = "Los Angeles",
    personalDetails = "",
    phone = "+1 (555) 123-4567",
  } = personalInfo || {};

  return (
    <section className="flex flex-col justify-start items-center py-4 px-6 bg-white">
      <header className="mb-10 text-center">
        <h2 className="text-xl font-semibold text-[#1D2639]">
          Personal Information
        </h2>
        <p className="text-sm font-medium text-[#7E7E7E] mt-1">
          This is a preview of the Personal Information section. Each field
          below represents an input type from the live form.
        </p>
      </header>

      <form className="flex flex-col w-full gap-8">
        {/* Profile Picture */}
        <fieldset>
          <label className="block text-[#1D2639] text-[12px] text-start font-normal mb-2">
            Profile Picture
          </label>
          <div className="flex items-center gap-4">
            <img
              src={profilePicture}
              alt=""
              className="w-24 h-24 rounded-full bg-gray-200 object-cover"
            />
            <div className="text-[#7E7E7E] text-sm italic">
              User uploads image here
            </div>
          </div>
        </fieldset>

        {/* Text Inputs */}
        {[
          { label: "First Name", value: firstName, type: "text" },
          { label: "Last Name", value: lastName, type: "text" },
        ].map(({ label, value, type }) => (
          <fieldset key={label}>
            <label className="block text-[#1D2639] text-[12px] text-start font-normal mb-2">
              {label}
            </label>
            <div className="w-full h-12 p-3 bg-[#F9F9F9] rounded-lg flex items-center text-gray-800">
              {value}
            </div>
          </fieldset>
        ))}

        {/* Country and State */}
        {[
          { label: "Country", value: country, type: "select" },
          { label: "State/Province", value: stateProvince, type: "select" },
          { label: "City", value: city, type: "text" },
        ].map(({ label, value, type }) => (
          <fieldset key={label}>
            <label className="block text-[#1D2639] text-[12px] text-start font-normal mb-2">
              {label}
            </label>
            <div className="w-full h-12 p-3 bg-[#F9F9F9]  rounded-lg flex items-center text-gray-800">
              {value}
            </div>
          </fieldset>
        ))}

        {/* Textarea */}
        <fieldset>
          <label className="block text-[#1D2639] text-[12px] text-start font-normal mb-2">
            Personal Details
          </label>
          <div className="w-full p-3 h-24 bg-[#F9F9F9]  rounded-lg text-gray-800 ">
            {personalDetails}
          </div>
        </fieldset>

        {/* Phone Number */}
        <fieldset>
          <label className="block text-[#1D2639] text-[12px] text-start font-normal mb-2">
            Phone Number
          </label>
          <div className="w-full h-12 p-3 bg-[#F9F9F9] rounded-lg flex items-center text-gray-800">
            {phone}
          </div>
        </fieldset>
      </form>
    </section>
  );
};

const BasicInformationPreview = () => {
  const basicInfo = useSelector((state) => state.formData.basicInfo || {});
  const [charCount, setCharCount] = useState(
    basicInfo.description ? basicInfo.description.length : 0
  );
  const charLimit = 250;

  useEffect(() => {
    setCharCount(basicInfo.description ? basicInfo.description.length : 0);
  }, [basicInfo.description]);

  return (
    <section className="flex flex-col justify-start items-center py-4 px-6 rounded-lg bg-white w-full">
      <header className="mb-8 text-center">
        <h2 className="text-xl font-semibold text-[#1D2639]">Supplier Info</h2>
        <p className="text-sm font-medium text-[#7E7E7E] mt-2">
          This is a preview of the Basic Information section. Each field below
          represents an input type from the live form.
        </p>
      </header>

      {[
        { label: "Company Name", value: basicInfo.name },
        { label: "Business Legal Name", value: basicInfo.legalName },
        { label: "DBA Name", value: basicInfo.dba },
        {
          label: "Business Description",
          value: basicInfo.description,
          type: "textarea",
        },
        { label: "Year Registered", value: basicInfo.foundedYear },
        { label: "Business Phone", value: basicInfo.businessPhone },
        { label: "Website URL", value: basicInfo.website },
      ].map(({ label, value, type = "text" }) => (
        <fieldset key={label} className="mb-6 w-full">
          <label className="block text-[#1D2639] text-[12px] text-start font-normal mb-2">
            {label}
          </label>
          {type === "textarea" ? (
            <>
              <textarea
                className="w-full h-24 p-3 bg-[#F9F9F9] rounded-lg"
                value={value || ""}
                readOnly
              />
              <div className="text-right text-sm text-[#7E7E7E]">
                {charCount}/{charLimit} characters
              </div>
            </>
          ) : (
            <input
              type={type}
              className="w-full h-12 p-3 bg-[#F9F9F9] rounded-lg "
              value={value || ""}
              readOnly
            />
          )}
        </fieldset>
      ))}
    </section>
  );
};

const BusinessLocationPreview = () => {
  const countries = [
    { code: "USA", name: "United States" },
    { code: "CAN", name: "Canada" },
  ];

  const statesUSA = [
    "Alabama",
    "Alaska",
    "Arizona",
    "Arkansas",
    "California",
    "Colorado",
  ];

  const provincesCanada = [
    "Alberta",
    "British Columbia",
    "Manitoba",
    "Ontario",
    "Quebec",
  ];

  const customStyles = {
    menu: (provided) => ({
      ...provided,
      textAlign: "start",
      background: "#F7F7F7",
      borderRadius: "0.5rem",
    }),
    control: (provided) => ({
      ...provided,
      border: "none",
      background: "#F9F9F9",
      height: "48px",
      borderRadius: "0.5rem",
      opacity: 0.8,
    }),
  };

  return (
    <section className="flex flex-col justify-start items-center py-4 px-6 bg-white">
      <header className="mb-10 text-center">
        <h2 className="text-xl font-semibold text-[#1D2639]">
          Business Location
        </h2>
        <p className="text-sm font-medium text-[#7E7E7E] mt-1">
          This is a preview of the Business Location Information section. Each
          field below represents an input type from the live form.
        </p>
      </header>

      <div className="flex flex-col w-full">
        {/* Country Selection */}
        <fieldset className="mb-6">
          <legend className="block text-[#1D2639] text-[12px] text-start font-normal mb-2">
            Select Country:
          </legend>
          <div className="flex gap-4">
            {countries.map((country) => (
              <label
                key={country.code}
                className="flex text-[#1D2639] text-[12px] text-start font-normal gap-2 mb-2"
              >
                <input
                  type="radio"
                  name="country"
                  value={country.code}
                  disabled
                />
                {country.name}
              </label>
            ))}
          </div>
        </fieldset>

        {/* Address */}
        <fieldset className="mb-6">
          <label
            htmlFor="address_line_01"
            className="block text-[#1D2639] text-[12px] text-start font-normal mb-2"
          >
            Address <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="address_line_01"
            placeholder="Enter Address"
            disabled
            className="w-full h-12 px-4 bg-[#F9F9F9] rounded-lg"
          />
        </fieldset>

        {/* City */}
        <fieldset className="mb-6">
          <label
            htmlFor="city"
            className="block text-[#1D2639] text-[12px] text-start font-normal mb-2"
          >
            City <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="city"
            placeholder="Enter City"
            disabled
            className="w-full h-12 px-4 bg-[#F9F9F9] rounded-lg"
          />
        </fieldset>

        {/* State/Province */}
        <fieldset className="mb-6">
          <label
            htmlFor="state"
            className="block text-[#1D2639] text-[12px] text-start font-normal mb-2"
          >
            State / Province <span className="text-red-500">*</span>
          </label>
          <Select
            id="state"
            name="state"
            isDisabled
            styles={customStyles}
            placeholder="Select State / Province"
            options={[
              ...statesUSA.map((s) => ({ label: s, value: s })),
              ...provincesCanada.map((p) => ({ label: p, value: p })),
            ]}
          />
        </fieldset>

        {/* Postal Code */}
        <fieldset className="mb-6">
          <label
            htmlFor="postal_code"
            className="block text-[#1D2639] text-[12px] text-start font-normal mb-2"
          >
            ZIP / Postal Code <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="postal_code"
            placeholder="Enter ZIP / Postal Code"
            disabled
            className="w-full h-12 px-4 bg-[#F9F9F9] rounded-lg"
          />
        </fieldset>
      </div>

      <div className="mt-8 opacity-50 pointer-events-none w-full">
        {/* <ContinueButton label="Save and Continue (Disabled)" /> */}
      </div>
    </section>
  );
};

const RegistrationCompliancePreview = () => {
  const registrationAndCompliance = useSelector(
    (state) => state.formData.registrationAndCompliance || {}
  );

  const complianceInfo = registrationAndCompliance.complianceInfo || {};
  const file = registrationAndCompliance.file;

  const customStyles = {
    menu: (provided) => ({
      ...provided,
      textAlign: "start",
      background: "#F7F7F7",
      borderRadius: "0.5rem",
    }),
    control: (provided) => ({
      ...provided,
      border: "none",
      background: "#F9F9F9",
      textAlign: "start",
      height: "48px",
      borderRadius: "0.5rem",
      boxShadow: "none",
      cursor: "not-allowed",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#1D2639",
    }),
    option: (provided) => ({
      ...provided,
      backgroundColor: provided.backgroundColor,
      color: "black",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#B0B0B0",
    }),
  };

  return (
    <section className="flex flex-col items-center justify-start bg-white py-4 px-6">
      <header className="text-center mb-14">
        <h2 className="text-xl font-semibold text-[#1D2639]">
          Registration & Compliance
        </h2>
        <p className="text-base font-medium text-[#7E7E7E] w-full  mt-2">
          This is a preview of the Registration & Compliance section. Each field
          below represents an input type from the live form.
        </p>
      </header>

      <form className="flex flex-col w-full  gap-4">
        {/* Tax ID / EIN */}
        <fieldset className="mb-4">
          <label className="block text-[#1D2639] text-[12px] text-start font-normal mb-2">
            Tax ID / EIN <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={complianceInfo?.ein?.code || ""}
            className="w-full h-12 p-3 bg-[#F9F9F9] rounded-lg"
            disabled
          />
        </fieldset>

        {/* DUNS Number */}
        <fieldset className="mb-4">
          <label className="block text-[#1D2639] text-[12px] text-start font-normal mb-2">
            DUNS Number
          </label>
          <input
            type="text"
            value={complianceInfo?.duns?.code || ""}
            className="w-full h-12 p-3 bg-[#F9F9F9] rounded-lg"
            disabled
          />
        </fieldset>

        {/* CAGE Code */}
        <fieldset className="mb-4">
          <label className="block text-[#1D2639] text-[12px] text-start font-normal mb-2">
            CAGE Code
          </label>
          <input
            type="text"
            value={complianceInfo?.cage?.code || ""}
            className="w-full h-12 p-3 bg-[#F9F9F9] rounded-lg"
            disabled
          />
        </fieldset>

        {/* Business Entity Type */}
        <fieldset className="mb-4">
          <label className="block text-[#1D2639] text-[12px] text-start font-normal mb-2">
            Select Business Entity Type <span className="text-red-500">*</span>
          </label>
          <Select
            options={Object.keys({
              "Sole Proprietorship": "Business License or DBA Certificate",
              "Limited Liability Company (LLC)":
                "Articles of Organization or Operating Agreement",
              "Corporation (C-Corp / S-Corp / B-Corp)":
                "Articles of Incorporation or Corporate Bylaws",
              "Partnership (General / Limited / LLP)":
                "Partnership Agreement or Certificate of Partnership",
              "Nonprofit Organization":
                "IRS Determination Letter (501(c)(3) or equivalent)",
            }).map((type) => ({ label: type, value: type }))}
            value={
              complianceInfo?.entityType
                ? {
                    label: complianceInfo.entityType,
                    value: complianceInfo.entityType,
                  }
                : null
            }
            styles={customStyles}
            isDisabled
            placeholder="Select Business Entity"
          />
        </fieldset>

        {/* Verification Document */}
        <fieldset className="mb-4">
          <label className="block text-[#1D2639] text-[12px] text-start font-normal mb-2">
            Upload Verification Document <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={file ? file.name || "Uploaded File" : ""}
            className="w-full h-12 p-3 bg-[#F9F9F9] rounded-lg text-gray-500"
            disabled
          />
        </fieldset>
      </form>
    </section>
  );
};

const industries = [
  { code: "11", name: "Agriculture, Forestry, Fishing and Hunting" },
  { code: "21", name: "Mining, Quarrying, and Oil and Gas Extraction" },
  { code: "22", name: "Utilities" },
  { code: "23", name: "Construction" },
  { code: "31-33", name: "Manufacturing" },
  { code: "42", name: "Wholesale Trade" },
  { code: "44-45", name: "Retail Trade" },
  { code: "48-49", name: "Transportation and Warehousing" },
  { code: "51", name: "Information" },
  { code: "52", name: "Finance and Insurance" },
  { code: "53", name: "Real Estate and Rental and Leasing" },
  { code: "54", name: "Professional, Scientific, and Technical Services" },
  { code: "55", name: "Management of Companies and Enterprises" },
  {
    code: "56",
    name: "Administrative and Support and Waste Management and Remediation Services",
  },
  { code: "61", name: "Educational Services" },
  { code: "62", name: "Health Care and Social Assistance" },
  { code: "71", name: "Arts, Entertainment, and Recreation" },
  { code: "72", name: "Accommodation and Food Services" },
  { code: "81", name: "Other Services (except Public Administration)" },
];

const ServicesCapabilitiesPreview = () => {
  const servicesAndCapabilities = useSelector(
    (state) => state.formData.servicesAndCapabilities || {}
  );

  const customStyles = {
    menu: (provided) => ({
      ...provided,
      textAlign: "start",
      background: "#F7F7F7",
    }),
    control: (provided) => ({
      ...provided,
      border: "none",
      background: "#F9F9F9",
      textAlign: "start",
      minHeight: "48px",
      height: "auto",
      boxShadow: "none",
      cursor: "not-allowed",
    }),
    singleValue: (provided) => ({ ...provided, color: "#1D2639" }),
    option: (provided) => ({
      ...provided,
      backgroundColor: provided.backgroundColor,
      color: "black",
    }),
    placeholder: (provided) => ({ ...provided, color: "#B0B0B0" }),
  };

  const customMultiSelectStyles = {
    ...customStyles,
    control: (provided) => ({ ...provided, cursor: "not-allowed" }),
  };

  return (
    <section className="flex flex-col justify-start items-center py-4 px-6">
      <header className="mb-14 text-center">
        <h2 className="text-xl font-semibold text-[#1D2639]">
          Services & Capabilities
        </h2>
        <p className="text-base font-medium text-[#7E7E7E] w-full mt-2">
          This is a preview of the Services & Capabilities section. Each field
          below represents an input type from the live form.
        </p>
      </header>

      <form className="flex flex-col w-full  gap-4">
        {/* Industry */}
        <label className="block text-[#1D2639] text-[12px] text-start font-normal mt-8 mb-2">
          Industry <span className="text-red-500">*</span>
        </label>
        <Select
          options={industries.map(({ code, name }) => ({
            value: code,
            label: name,
          }))}
          value={
            servicesAndCapabilities.industry
              ? {
                  value: servicesAndCapabilities.industry,
                  label: servicesAndCapabilities.industry,
                }
              : null
          }
          styles={customStyles}
          placeholder="Select an Industry..."
          isDisabled
        />

        {/* Primary NAICS */}
        <label className="block text-[#1D2639] text-[12px] text-start font-normal mt-8 mb-2">
          Primary NAICS Code <span className="text-red-500">*</span>
        </label>
        <Select
          options={servicesAndCapabilities.naicsData || []}
          value={
            servicesAndCapabilities.primaryNAICS
              ? {
                  value: servicesAndCapabilities.primaryNAICS.code,
                  label: servicesAndCapabilities.primaryNAICS.name,
                }
              : null
          }
          styles={customStyles}
          placeholder="Select Primary NAICS..."
          isDisabled
        />

        {/* Additional NAICS */}
        {servicesAndCapabilities.additionalNAICS?.length > 0 && (
          <>
            <label className="block text-[#1D2639] text-[12px] text-start font-normal mt-8 mb-2">
              Additional NAICS Codes
            </label>
            <Select
              options={servicesAndCapabilities.naicsData || []}
              value={servicesAndCapabilities.additionalNAICS.map((naic) => ({
                value: naic.code,
                label: naic.name,
              }))}
              styles={customMultiSelectStyles}
              isMulti
              placeholder="Select Additional NAICS Codes..."
              isDisabled
            />
          </>
        )}
      </form>
    </section>
  );
};

const DiversityVerificationPreview = ({ diversityCerts = [] }) => {
  return (
    <section className="flex flex-col items-center justify-start bg-white py-4 px-6">
      <header className="text-center mb-14">
        <h2 className="text-xl font-semibold text-[#1D2639]">
          Diversity Data & Verification
        </h2>
        <p className="text-base font-medium text-[#7E7E7E] w-full  mt-2">
          This is a preview of the Diversity Verification section. Users will be
          able to select diversity classifications, enter certification IDs,
          choose agencies, and upload supporting files in the live form.
        </p>
      </header>

      <form className="flex flex-col w-full  gap-6" aria-live="polite">
        <div>
          <label className="block text-[#1D2639] text-[16px] font-normal mb-2">
            Diversity-Owned Business Categories{" "}
          </label>
          <div className="bg-[#F9F9F9] p-4">
            <CollapsibleSections />
          </div>
        </div>

        {/* Certification Table */}
        {diversityCerts.length > 0 ? (
          <div className="overflow-x-scroll w-full ">
            <table className="border-collapse border-t border-b border-[#EEE] mt-4 w-full max-w-[1200px] mx-auto whitespace-nowrap">
              <thead>
                <tr>
                  <th className="border-r border-[#EEE] px-4 py-2 text-[#757575] bg-[#FAFAFA] text-xs sm:text-sm">
                    Selected Certification
                  </th>
                  <th className="border-r border-[#EEE] px-4 py-2 text-[#757575] bg-[#FAFAFA] text-xs sm:text-sm">
                    Cert ID
                  </th>
                  <th className="border-r border-[#EEE] px-4 py-2 text-[#757575] bg-[#FAFAFA] text-xs sm:text-sm w-56">
                    Agency
                  </th>
                  <th className="border-r border-[#EEE] px-4 py-2 text-[#757575] bg-[#FAFAFA] text-xs sm:text-sm">
                    File Upload
                  </th>
                </tr>
              </thead>
              <tbody>
                {diversityCerts.map((entry, index) => (
                  <tr
                    key={index}
                    className="border-t border-b border-[#EEE] text-left"
                  >
                    <td className="border-r border-[#EEE] px-2 py-2 text-xs sm:text-sm text-gray-800">
                      {entry.certName || (
                        <span className="text-[#7E7E7E] italic">
                          Certification name
                        </span>
                      )}
                    </td>
                    <td className="border-r border-[#EEE] px-2 py-2 text-xs sm:text-sm text-gray-800">
                      <div className="rounded bg-[#F9F9F9 p-2">
                        {entry.certId || (
                          <span className="text-[#7E7E7E] italic">
                            Example: 12345-ABC
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="border-r border-[#EEE] px-2 py-2 w-56 text-xs sm:text-sm text-gray-800">
                      <div className="rounded bg-[#F9F9F9] p-2">
                        {entry.agency || (
                          <span className="text-[#7E7E7E] italic">
                            Select from dropdown
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="border-r border-[#EEE] px-2 py-2 text-xs sm:text-sm text-gray-800">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-[#F9F9F9] rounded" />
                        <span className="text-[#7E7E7E] italic">
                          Upload file (PDF, JPG)
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-[#F9F9F9] rounded-lg p-4 text-[#7E7E7E] text-sm">
            No certifications added
          </div>
        )}
      </form>
    </section>
  );
};

const TestimonialsReferencesPreview = () => {
  const testimonialsAndReferences = useSelector(
    (state) => state.formData.testimonialsAndReferences || []
  );

  const [entries, setEntries] = useState([]);

  useEffect(() => {
    if (testimonialsAndReferences.length > 0) {
      setEntries(
        testimonialsAndReferences.map((value) => ({
          type: value.reference ? "Professional References" : "Client",
          name: value.endorsementInfo?.name || "",
          feedback: value.endorsementInfo?.feedback || "",
          contact: value.endorsementInfo?.contact || "",
          fileName: value.file ? value.file.name : null,
        }))
      );
    } else {
      setEntries([
        { type: "Client", name: "", feedback: "", contact: "", fileName: null },
      ]);
    }
  }, [testimonialsAndReferences]);

  return (
    <section className="flex flex-col items-center justify-start bg-white py-4 px-6">
      <header className="text-center mb-14">
        <h2 className="text-xl font-semibold text-[#1D2639]">
          Testimonials & References
        </h2>
        <p className="text-base font-medium text-[#7E7E7E] w-full mt-2">
          This is a preview of the Testimonials & References section. Each field
          below represents an input type from the live form.
        </p>
      </header>

      <form className="flex flex-col w-full gap-8">
        {entries.map((entry, index) => (
          <fieldset key={index} className="mb-8">
            <div className="flex justify-between">
              <label className="block text-[#1D2639] text-[12px] text-start font-normal mb-2">
                Type
              </label>
            </div>

            <div className="flex gap-4">
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  checked={entry.type === "Client"}
                  disabled
                />
                Client
              </label>
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  checked={entry.type === "Professional References"}
                  disabled
                />
                Professional References
              </label>
            </div>

            <label className="block text-[#1D2639] text-[12px] text-start font-normal mt-4 mb-2">
              Name
            </label>
            <input
              type="text"
              className="w-full h-12 p-3 bg-[#F9F9F9] rounded-lg"
              value={entry.name}
              disabled
            />

            {entry.type === "Client" ? (
              <>
                <label className="block text-[#1D2639] text-[12px] text-start font-normal mt-4 mb-2">
                  Feedback
                </label>
                <textarea
                  className="w-full h-24 p-3 bg-[#F9F9F9] rounded-lg"
                  value={entry.feedback}
                  disabled
                />
              </>
            ) : (
              <>
                <label className="block text-[#1D2639] text-[12px] text-start font-normal mt-4 mb-2">
                  Contact Info
                </label>
                <input
                  type="text"
                  className="w-full h-12 p-3 bg-[#F9F9F9] rounded-lg"
                  value={entry.contact}
                  disabled
                />
              </>
            )}

            <label className="block text-[#1D2639] text-[12px] text-start font-normal mt-4 mb-2">
              Uploaded Document
            </label>
            <input
              type="text"
              className="w-full h-12 p-3 bg-[#F9F9F9] rounded-lg text-gray-500"
              value={entry.fileName || "No file uploaded"}
              disabled
            />
          </fieldset>
        ))}
      </form>
    </section>
  );
};

const categories = [
  {
    title: "Procurement & Supply Chain Management Organizations",
    options: [
      "American Purchasing Society (APS)",
      "Asia Pacific Procurement Confederation (APPC)",
      "Association for Supply Chain Management (ASCM)",
      "Australian Procurement and Construction Council (APCC)",
      "Canadian Public Procurement Council (CPPC)",
      "Chartered Institute of Procurement & Supply (CIPS)",
      "European Institute of Purchasing Management (EIPM)",
      "Institute for Supply Management (ISM)",
      "Institute of Supply Chain Management (IoSCM)",
      "International Association of Procurement Professionals (IAPP)",
      "International Federation of Purchasing and Supply Management (IFPSM)",
      "National Association of Educational Procurement (NAEP)",
      "National Institute of Governmental Purchasing (NIGP)",
      "Next Level Purchasing Association (NLPA)",
      "Procurement and Supply Chain Management Association of the Philippines (PASIA)",
      "Procurement Leaders",
      "Supply Chain Canada",
    ],
  },
  {
    title: "Government & Public Procurement Organizations",
    options: [
      "Institute for Public Procurement (NIGP)",
      "Institute of Governmental Purchasing (IGP)",
      "Higher Education",
      "National Association of State Procurement Officials (NASPO)",
      "National Procurement Institute (NPI)",
      "The Universal Public Procurement Certification Council (UPPCC)",
    ],
  },
  {
    title: "Contracting & Sourcing Organizations",
    options: [
      "Global Sourcing Association (GSA)",
      "International Association for Contract and Commercial Management (IACCM)",
      "National Contract Management Association (NCMA)",
      "World Commerce & Contracting (WorldCC)",
    ],
  },
  {
    title: "Small Business & Minority Business Organizations",
    options: [
      "Minority Business Development Agency (MBDA)",
      "National Association of Minority Contractors (NAMC)",
      "National Association of Small Business Contractors (NASBC)",
      "National Association of Women Business Owners (NAWBO)",
      "National Black Chamber of Commerce (NBCC)",
      "National Center for American Indian Enterprise Development (NCAIED)",
      "National Women Business Owners Corporation (NWBOC)",
      "U.S. Pan Asian American Chamber of Commerce (USPAACC)",
      "U.S. Small Business Administration (SBA)",
      "United States Hispanic Chamber of Commerce (USHCC)",
      "Asian/Pacific Islander American Chamber of Commerce & Entrepreneurship (ACE)",
    ],
  },
  {
    title: "State-Level Government & Administrative Organizations",
    options: [
      "National Association of State Auditors, Comptrollers and Treasurers (NASACT)",
      "National Association of State Budget Officers (NASBO)",
      "National Association of State Chief Administrators (NASCA)",
      "National Association of State Chief Information Officers (NASCIO)",
      "National Association of State Facilities Administrators (NASFA)",
      "National Association of State Personnel Executives (NASPE)",
      "National Association of State Technology Directors (NASTD)",
      "National Association of State Treasurers (NAST)",
      "National Association of State Workforce Agencies (NASWA)",
      "National Association of State Agencies for Surplus Property (NASASP)",
    ],
  },
  {
    title: "Logistics & Financial Infrastructure",
    options: [
      "International Warehouse Logistics Association (IWLA)",
      "Society for Worldwide Interbank Financial Telecommunication (SWIFT)",
    ],
  },
];

const MembershipCertificationsPreview = () => {
  const { membershipsCertifications } = useSelector(
    (state) => state.formData || {}
  );
  const { memberships = [], certifications = [] } =
    membershipsCertifications || {};
  const [selectedCheckboxes, setSelectedCheckboxes] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});

  // Mark selected checkboxes
  useEffect(() => {
    const selected = {};
    const processItems = (items) => {
      items.forEach((item) => {
        const fullName = `${item.name} (${item.acronym})`;
        selected[fullName] = true;
      });
    };
    processItems(memberships);
    processItems(certifications);
    setSelectedCheckboxes(selected);
  }, [memberships, certifications]);

  // Toggle category open/closed
  const toggleCategory = (title) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  return (
    <section className="flex flex-col items-center  justify-start bg-white py-4 px-0 sm:px-6">
      <header className="text-center mb-6">
        <h2 className="text-xl font-semibold text-[#1D2639] lg:whitespace-nowrap">
          Membership Associations & Certifications
        </h2>
        <p className="text-base font-medium text-[#7E7E7E] w-full mx-auto mt-2">
          This is a preview of the Membership Associations & Certifications
          section. Each field below represents an input type from the live form.
        </p>
      </header>

      <form className="flex flex-col flex-start justify-center items-start w-full gap-6 p-6 rounded-lg">
        <fieldset className="border-none">
          <legend className="sr-only">
            Membership Associations & Certifications
          </legend>

          {categories.map((category) => (
            <div key={category.title} className="mb-8">
              <div
                className="flex items-center justify-start cursor-pointer mb-2"
                onClick={() => toggleCategory(category.title)}
              >
                {expandedCategories[category.title] ? (
                  <ChevronDown />
                ) : (
                  <ChevronRight />
                )}
                <h3 className="text-base text-start font-base ml-2">
                  {category.title}
                </h3>
              </div>

              {expandedCategories[category.title] && (
                <ul>
                  {category.options.map((option) => (
                    <li
                      key={option}
                      className="flex items-center justify-start text-gray-500 whitespace-nowrap mb-2"
                    >
                      <input
                        type="checkbox"
                        checked={selectedCheckboxes[option] || false}
                        disabled
                        className="checkbox-option flex items-center justify-start text-gray-500 mr-4"
                      />
                      {option}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </fieldset>
      </form>
    </section>
  );
};

const RegistrationForm = ({ handleCreate, setFormTitle }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [formName, setFormName] = useState("");

  const closeModal = () => setIsOpen(false);
  const createForm = () => {
    setFormTitle(formName);
    closeModal();
  };

  const [selectedForm, setSelectedForm] = useState("standard");

  const forms = [
    {
      id: "standard",
      title: "Standard Supplier Form",
      description: "General purpose supplier registration",
      disabled: false,
    },
    {
      id: "pre",
      title: "Pre-Registration Form",
      description: "Collect initial information before full onboarding",
      disabled: true,
    },
    {
      id: "post",
      title: "Post-Registration Form",
      description: "Additional details after initial registration",
      disabled: true,
    },
  ];
  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-200"
          style={{ zIndex: 1000 }}
          onClose={() => {}}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="fixed inset-0 bg-black bg-opacity-30" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-xl rounded-lg bg-white p-6 shadow-xl">
              <Dialog.Title className="flex flex-row gap-4 items-centertext-lg font-semibold text-[24px] text-gray-900 mb-4">
                <div className="bg-[#D1D1D1] p-2 rounded">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                  >
                    <path
                      d="M9.77713 13.3337H15.1105V15.1114H9.77713V13.3337ZM7.99935 6.22255H6.22157V8.00033H7.99935V6.22255ZM9.77713 11.5559H15.1105V9.7781H9.77713V11.5559ZM9.77713 8.00033H15.1105V6.22255H9.77713V8.00033ZM7.99935 9.7781H6.22157V11.5559H7.99935V9.7781ZM18.666 4.44477V16.8892C18.666 17.867 17.866 18.667 16.8882 18.667H4.44379C3.46602 18.667 2.66602 17.867 2.66602 16.8892V4.44477C2.66602 3.46699 3.46602 2.66699 4.44379 2.66699H16.8882C17.866 2.66699 18.666 3.46699 18.666 4.44477ZM16.8882 4.44477H4.44379V16.8892H16.8882V4.44477ZM7.99935 13.3337H6.22157V15.1114H7.99935V13.3337Z"
                      fill="white"
                    />
                  </svg>
                </div>
                Create New Supplier Form
              </Dialog.Title>
              <span className="mt-2 mb-2 text-[#7E7E7E] text-base">
                Choose a form type and give your form a name
              </span>

              {/* Form Name Input */}
              <div className="mb-6 mt-4">
                <label
                  htmlFor="formName"
                  className="block text-base font-medium text-[#1D2739] mb-1"
                >
                  Form Name
                </label>
                <input
                  id="formName"
                  type="text"
                  placeholder="e.g., Supplier Onboarding Form"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full bg-[#F4F4F4] rounded-md border-none py-2 px-2"
                />
              </div>

              <div className="space-y-2 mb-6">
                {forms.map((form) => (
                  <label
                    key={form.id}
                    className={`flex items-center border rounded-md p-3 cursor-pointer ${
                      form.disabled
                        ? "bg-gray-100 opacity-50 cursor-not-allowed"
                        : selectedForm === form.id
                        ? "bg-white ring-2 ring-blue-500"
                        : "bg-white"
                    }`}
                  >
                    <input
                      type="checkbox"
                      disabled={form.disabled}
                      checked={selectedForm === form.id}
                      onChange={() => setSelectedForm(form.id)}
                      className="w-5 h-5 rounded-full border-2 border-gray-300 checked:bg-blue-500 checked:border-blue-500 text-white flex-shrink-0"
                    />
                    <div className="flex flex-col ml-3">
                      <span
                        className={`font-medium ${
                          form.disabled ? "text-gray-500" : "text-gray-800"
                        }`}
                      >
                        {form.title}
                      </span>
                      <span
                        className={`text-sm ${
                          form.disabled ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {form.description}
                      </span>
                    </div>
                  </label>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 rounded-md text-base text-gray-700 bg-gray-100 hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={createForm}
                  className="px-4 py-2 rounded-md text-base text-balck bg-[#EAF524]"
                >
                  Create Form
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </Transition>
      <section className="flex flex-col items-center justify-start py-6 px-6 bg-white">
        <h1 className="text-2xl font-semibold text-[#1D2639] text-center mb-8 mt-10">
          Supplier Registration Form (Preview)
        </h1>

        <div className="border border-gray-100 rounded-lg w-full max-w-6xl flex flex-col gap-4 p-6 shadow-lg">
          <PersonalInfoPreview />
          <BasicInformationPreview />
          <BusinessLocationPreview />
          <RegistrationCompliancePreview />
          <ServicesCapabilitiesPreview />
          <DiversityVerificationPreview />
          <TestimonialsReferencesPreview />
          <MembershipCertificationsPreview />
        </div>

        <div className="w-full flex justify-center mt-6">
          <button
            type="button"
            className="bg-[#eaf524] flex justify-center cursor-pointer items-center self-center w-[432px] h-14 p-5 mt-14
        rounded-[13px] text-center text-base font-medium"
            onClick={handleCreate}
          >
            Generate Private URL
          </button>
        </div>
      </section>
    </>
  );
};

export default RegistrationForm;
